// @file Surface wish arrangement store
import { getAuthorDisplayName } from '@@/bits/author'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { BLANK_GROUP, ORDERED_COLOR_GROUPS } from '@@/bits/surface_posts_grouping_helper'
import type { ManualSortBy } from '@@/bits/surface_settings_helper'
import { ManualSortByArray } from '@@/bits/surface_settings_helper'
import type { WallWishSortByTypes } from '@@/enums'
import { WallWishGroupByTypes, WallWishGroupByValue } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import type {
  LabelledSection,
  WallCustomPostProperty,
  WallCustomPostSingleSelectProperty,
  WallGroupAuthor,
  WallGroupColor,
  WallGroupNone,
  WallGroupPost,
  WallGroupSingleSelect,
  WallWishGroupBy,
  WallWishGroupByCustomProp,
  WishArrangement,
} from '@@/types'
import { SortByTypes } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useSurfaceWishArrangementStore = defineStore('surfaceWishArrangement', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia

  const surfaceStore = useSurfaceStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfaceSettingsStore = useSurfaceSettingsStore()
  const surfaceUserContributorsStore = useSurfaceUserContributorsStore()
  const surfacePostPropertiesStore = useSurfacePostPropertiesStore()

  const originalWishArrangement = computed<WishArrangement | undefined>(
    () => surfaceVuexStore?.state.wall.wish_arrangement,
  )
  const previewWishArrangement = computed<WishArrangement | undefined>(
    () => surfaceSettingsStore.previewAttributes.wish_arrangement,
  )

  const wishGroupBy = computed<WallWishGroupBy>(() => {
    if (!surfaceStore.canUseSections) return WallWishGroupByTypes.None
    const groupByValue = surfaceSettingsStore.isPreviewing
      ? surfaceSettingsStore.wishGroupBy
      : surfaceVuexStore?.getters.wishGroupBy
    if (surfaceStore.isColumns && groupByValue === WallWishGroupByTypes.None) return WallWishGroupByTypes.Section
    return groupByValue
  })
  const isSectionedAndNonmanualSorted = computed<boolean>(
    () => !areWishesManuallySorted.value && surfaceStore.isSectioned,
  )
  const isNotSectionedAndNonmanualSorted = computed<boolean>(
    () => !areWishesManuallySorted.value && !surfaceStore.isSectioned,
  )
  const isSectionable = computed<boolean>(() => {
    if (surfaceStore.isColumns) return true
    return surfaceStore.wallAttributes.wish_arrangement?.is_sectionable ?? false
  })
  const originalWishSortBy = computed<WallWishSortByTypes>(() => surfaceVuexStore?.getters.wishSortBy)
  const previewWishSortBy = computed<WallWishSortByTypes>(() => surfaceSettingsStore.wishSortBy)
  const hasWishSortByChanged = computed(() => previewWishSortBy.value !== originalWishSortBy.value)
  const defaultWishSortBy = computed<SortByTypes.ManualNewPostsLast | SortByTypes.ManualNewPostsFirst>(() => {
    if (surfaceStore.isTimeline || surfaceStore.isShelf) return SortByTypes.ManualNewPostsLast
    return SortByTypes.ManualNewPostsFirst
  })
  const areWishesManuallySorted = computed<boolean>(() =>
    ManualSortByArray.includes(originalWishSortBy.value as ManualSortBy),
  )

  /**
   * Determines whether wishes are currently not grouped (grouped by None).
   * Returns true when no grouping is applied, and false when wishes are
   * grouped by section, author, or color.
   */
  const isGroupByNone = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.None
  })

  const isGroupBySection = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.Section
  })

  const isGroupByColor = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.Color
  })

  const isGroupByAuthor = computed<boolean>(() => {
    return wishGroupBy.value === WallWishGroupByTypes.Author
  })

  const isGroupBySingleSelect = computed(() => {
    if (isGroupByNone.value || isGroupBySection.value || isGroupByColor.value || isGroupByAuthor.value) return false
    return true
  })

  const customPropIdFromWishGroupBy = computed((): WallCustomPostSingleSelectProperty['id'] | null => {
    if (!isGroupBySingleSelect.value) return null
    return wishGroupBy.value.split(':')[1] as WallCustomPostProperty['id']
  })

  const sortedGroups = computed<WallGroupPost[]>(() => {
    switch (wishGroupBy.value) {
      case WallWishGroupByTypes.Section:
        return surfaceSectionsStore.sortedSections as LabelledSection[]
      case WallWishGroupByTypes.Color:
        return ORDERED_COLOR_GROUPS as WallGroupColor[]
      case WallWishGroupByTypes.Author:
        return Object.values(surfaceUserContributorsStore.postAuthorsById)
          .map((author) => {
            return {
              id: author.id,
              title: getAuthorDisplayName(author),
              avatar: author.avatar,
            }
          })
          .sort((a, b) => (a.title || '').localeCompare(b.title || '')) as WallGroupAuthor[]
      case WallWishGroupByTypes.None:
        return [BLANK_GROUP] as WallGroupNone[]
      default: {
        // Group by single select custom property
        if (customPropIdFromWishGroupBy.value == null) return []

        const singleSelectProperty =
          surfacePostPropertiesStore.wallSingleSelectPropsByCustomPropertyId[customPropIdFromWishGroupBy.value]
        if (singleSelectProperty?.selection_options == null) return []

        const singleSelectOptions = singleSelectProperty.selection_options.map((option) => ({
          id: option.id,
          title: option.name,
        })) as WallGroupSingleSelect[]

        return [...singleSelectOptions, { id: WallWishGroupByValue.NoValue, title: __('No value') }]
      }
    }
  })

  // Actions

  const isWishGroupedBy = (groupId: WallGroupPost['id']): boolean => {
    return wishGroupBy.value === groupId || wishGroupBy.value === getWishGroupByValueFromCustomPropId(groupId as string)
  }

  const getWishGroupByValueFromCustomPropId = (
    singleSelectId: WallCustomPostSingleSelectProperty['id'],
  ): WallWishGroupByCustomProp => {
    return `custom_prop:${singleSelectId ?? ''}`
  }

  return {
    // getters
    isNotSectionedAndNonmanualSorted,
    isSectionedAndNonmanualSorted,
    wishGroupBy,
    defaultWishSortBy,
    isGroupByNone,
    isGroupBySection,
    isGroupByColor,
    isGroupByAuthor,
    isGroupBySingleSelect,
    customPropIdFromWishGroupBy,
    sortedGroups,
    originalWishSortBy,
    previewWishSortBy,
    originalWishArrangement,
    previewWishArrangement,
    hasWishSortByChanged,
    areWishesManuallySorted,
    isSectionable,

    // actions
    isWishGroupedBy,
    getWishGroupByValueFromCustomPropId,
  }
})
